import React, { useState, useEffect } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { auth } from "@/shared/api/graphqlClient";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  ArrowLeft,
  Save,
  User,
  Shield,
  Eye,
  EyeOff,
  CheckCircle2,
  Plus,
  RefreshCw,
} from "lucide-react";
import { Checkbox } from "@/shared/components/ui/checkbox";

function generatePassword() {
  const length = 10;
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const special = "!@#$%^&*()_+";
  const all = upper + lower + digits + special;

  let password = "";
  password += upper.charAt(Math.floor(Math.random() * upper.length));
  password += lower.charAt(Math.floor(Math.random() * lower.length));
  password += digits.charAt(Math.floor(Math.random() * digits.length));
  password += special.charAt(Math.floor(Math.random() * special.length));

  for (let i = password.length; i < length; i++) {
    password += all.charAt(Math.floor(Math.random() * all.length));
  }

  // Shuffle
  return password.split('').sort(() => 0.5 - Math.random()).join('');
}

export default function AdminStaffForm() {
  const { t } = useLanguage();
  const urlParams = new URLSearchParams(window.location.search);
  const staffId = urlParams.get("id");
  const isEdit = !!staffId;

  // Helper function to translate permission keys
  const translatePermission = (permissionKey) => {
    const translationKey = `permission.${permissionKey}`;
    console.log("translationKey : ", translationKey)

    const translated = t(translationKey);
    console.log("translated : ", translated)

    // If translation exists, return it; otherwise return the original key formatted nicely
    if (translated !== translationKey) {
      return translated;
    }
    // Fallback: format the key nicely (e.g., "merchants.view" -> "Merchants View")
    return permissionKey
      .split('.')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to translate role names
  const translateRoleName = (roleName) => {
    const translationKey = `role.${roleName}`;
    const translated = t(translationKey);
    // If translation exists, return it; otherwise return the original name
    return translated !== translationKey ? translated : roleName;
  };

  // Helper function to translate role descriptions
  const translateRoleDescription = (description) => {
    if (!description) return '';
    const translationKey = `roleDesc.${description}`;
    const translated = t(translationKey);
    // If translation exists, return it; otherwise return the original description
    return translated !== translationKey ? translated : description;
  };

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [currentUserId, setCurrentUserId] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    status: "ACTIVE",
    parentType: "ADMIN",
    parentId: "",
    roleIds: [],
  });

  useEffect(() => {
    const loadCurrentUser = async () => {
      setLoadingUser(true);
      try {
        const user = await auth.me();
        console.log("Current user from auth.me():", user);
        // For admin, getUserData returns { userId, email, name }
        const userId = user?.userId || user?.id;
        console.log("Extracted userId:", userId);
        if (userId) {
          setCurrentUserId(userId);
          setFormData(prev => ({ ...prev, parentId: userId }));
        } else {
          console.warn("No userId found in user object:", user);
          // Fallback to getUserData if me() fails
          const userData = auth.getUserData();
          console.log("Fallback userData:", userData);
          if (userData?.userId) {
            setCurrentUserId(userData.userId);
            setFormData(prev => ({ ...prev, parentId: userData.userId }));
          }
        }
      } catch (error) {
        console.error("Failed to load current user:", error);
        // Fallback to getUserData if me() fails
        const userData = auth.getUserData();
        console.log("Fallback userData (error case):", userData);
        if (userData?.userId) {
          setCurrentUserId(userData.userId);
          setFormData(prev => ({ ...prev, parentId: userData.userId }));
        }
      } finally {
        setLoadingUser(false);
      }
    };
    if (!isEdit) {
      loadCurrentUser();
    } else {
      setLoadingUser(false);
    }
  }, [isEdit]);

  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({ name: false, email: false, password: false, roles: false });

  const { data: existingStaff } = useQuery({
    queryKey: ["staff", staffId],
    queryFn: () => adminService.getStaffUserById(staffId),
    enabled: isEdit,
  });

  const { data: availableRoles = [] } = useQuery({
    queryKey: ["available-roles"],
    queryFn: () => adminService.getAvailableRoles(),
  });

  useEffect(() => {
    if (existingStaff) {
      setFormData(prev => ({
        ...prev,
        name: existingStaff.name,
        email: existingStaff.email,
        password: "",
        status: existingStaff.status,
        parentType: existingStaff.parentType,
        parentId: existingStaff.parentId,
        roleIds: existingStaff.roles?.map(r => r.id) || [],
      }));
    }
  }, [existingStaff]);

  const createMutation = useMutation({
    mutationFn: (data) => adminService.createStaffUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-staff"]);
      toast.success(t("staffMemberCreatedSuccessfully", "Staff member created successfully"));
      navigate(createPageUrl("AdminStaff"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminService.updateStaffUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-staff"]);
      queryClient.invalidateQueries(["staff", staffId]);
    },
  });

  const assignRoleMutation = useMutation({
    mutationFn: (input) => adminService.assignRoleToStaff(input),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-staff"]);
      queryClient.invalidateQueries(["staff", staffId]);
    },
  });

  const removeRoleMutation = useMutation({
    mutationFn: (input) => adminService.removeRoleFromStaff(input),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-staff"]);
      queryClient.invalidateQueries(["staff", staffId]);
    },
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleRoleToggle = (roleId) => {
    setFormData((prev) => {
      const roleIds = prev.roleIds || [];
      if (roleIds.includes(roleId)) {
        return { ...prev, roleIds: roleIds.filter(id => id !== roleId) };
      } else {
        const updated = [...roleIds, roleId];
        if (errors.roles) setErrors((prev) => ({ ...prev, roles: false }));
        return { ...prev, roleIds: updated };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEdit && !formData.parentId) {
      toast.error(t("unableToGetCurrentUserId"));
      return;
    }

    // Granular field validation
    const newErrors = {
      name: !formData.name.trim(),
      email: !formData.email.trim(),
      password: !isEdit && !formData.password.trim(),
      roles: formData.roleIds.length === 0,
    };
    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some(Boolean);
    if (hasErrors) {
      if (newErrors.roles) {
        toast.error("You must select at least one role");
      }
      return;
    }

    if (!isEdit && formData.password.length < 6) {
      toast.error(t("passwordMustBeAtLeast6Characters"));
      return;
    }

    setSaving(true);

    try {
      if (isEdit) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          status: formData.status,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await updateMutation.mutateAsync({ id: staffId, data: updateData });

        // Update roles
        const initialRoles = existingStaff?.roles?.map(r => r.id) || [];
        const currentRoles = formData.roleIds || [];

        const rolesToAdd = currentRoles.filter(roleId => !initialRoles.includes(roleId));
        const rolesToRemove = initialRoles.filter(roleId => !currentRoles.includes(roleId));

        for (const roleId of rolesToAdd) {
          await assignRoleMutation.mutateAsync({ staffUserId: staffId, roleId });
        }
        for (const roleId of rolesToRemove) {
          await removeRoleMutation.mutateAsync({ staffUserId: staffId, roleId });
        }

        toast.success(t("staffMemberUpdatedSuccessfully", "Staff member updated successfully"));
        navigate(createPageUrl("AdminStaff"));
      } else {
        const input = {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          parentType: formData.parentType,
          parentId: formData.parentId,
        };

        if (formData.roleIds && formData.roleIds.length > 0) {
          input.roleIds = formData.roleIds;
        }

        console.log("Creating staff user with input:", { ...input, password: "***" });
        await createMutation.mutateAsync(input);
      }
    } catch (error) {
      console.error("Failed to save staff:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loadingUser && !isEdit) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-500">{t("loadingUserInformation")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={createPageUrl("AdminStaff")}>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEdit ? t("editStaffMember") : t("createStaffMember")}
          </h1>
          <p className="text-slate-500">
            {isEdit ? t("updateStaffMemberDetails") : t("addNewStaffMemberToOrganization")}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} autoComplete="off">
        <div className="grid gap-6">
          {/* General Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t("generalInformation")}</CardTitle>
              <CardDescription>{t("basicDetailsForStaffMember")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("fullName")} *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder=""
                    autoComplete="off"
                    className={errors.name ? "border-red-500 focus-visible:ring-red-500" : ""}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1">Full name is required</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t("emailAddress")} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder=""
                    autoComplete="off"
                    className={errors.email ? "border-red-500 focus-visible:ring-red-500" : ""}
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1">Email address is required</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  {isEdit ? t("newPasswordLeaveBlank") : `${t("password")} *`}
                </Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => handleChange("password", e.target.value)}
                      placeholder=""
                      minLength={6}
                      autoComplete="new-password"
                      className={`pr-10 ${errors.password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 text-slate-400 hover:text-slate-600 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-10 px-3 flex items-center gap-2 group border border-blue-100 hover:border-blue-600 hover:bg-blue-600 hover:text-white text-blue-600 font-medium transition-all duration-300 rounded-lg shadow-sm w-full sm:w-auto flex-shrink-0"
                    onClick={() => {
                      const newPass = generatePassword();
                      handleChange("password", newPass);
                      navigator.clipboard.writeText(newPass);
                    }}
                  >
                    <RefreshCw className="h-4 w-4 group-hover:rotate-180 transition-transform duration-500" />
                    <span className="text-xs uppercase tracking-wide">{t("generatePassword")}</span>
                  </Button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-500 mt-1">Password is required (min 6 characters)</p>
                )}
              </div>

              {isEdit && (
                <div className="space-y-2">
                  <Label htmlFor="status">{t("status")}</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleChange("status", value)}
                  >
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">{t("active")}</SelectItem>
                      <SelectItem value="INACTIVE">{t("inactive")}</SelectItem>
                      <SelectItem value="SUSPENDED">{t("suspended")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Role Assignment */}
          <Card className={errors.roles ? "border-red-500 ring-1 ring-red-500" : ""}>
            <CardHeader>
              <CardTitle>{t("roleAssignment")}</CardTitle>
              <CardDescription>{t("assignRolesToControlAccess")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {availableRoles.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                  <Shield className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-sm text-slate-500 mb-4">{t("noRolesAvailableCreateFirst")}</p>
                  <Link to={createPageUrl("AdminRoleForm")}>
                    <Button variant="outline" className="gap-2">
                      <Plus className="h-4 w-4" />
                      {t("createRole")}
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableRoles.map((role) => {
                    const isAssigned = formData.roleIds?.includes(role.id);

                    console.log("role : ", role.name)
                    return (
                      <div key={role.id} className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-slate-50">
                        <Checkbox
                          id={`role-${role.id}`}
                          checked={isAssigned}
                          onCheckedChange={() => handleRoleToggle(role.id)}
                          disabled={assignRoleMutation.isPending || removeRoleMutation.isPending}
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor={`role-${role.id}`}
                            className="font-medium cursor-pointer flex items-center gap-2"
                          >
                            <Shield className="h-4 w-4 text-blue-600" />
                            {translateRoleName(role.name)}
                          </Label>
                          {role.description && (
                            <p className="text-sm text-slate-500 mt-1">{translateRoleDescription(role.description)}</p>
                          )}
                          {role.permissions && role.permissions.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {role.permissions.slice(0, 5).map((perm, idx) => (
                                <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                  {translatePermission(perm)}
                                </span>
                              ))}
                              {role.permissions.length > 5 && (
                                <span className="text-xs text-slate-400">
                                  +{role.permissions.length - 5} {t("morePermissions")}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Link to={createPageUrl("AdminStaff")}>
              <Button type="button" variant="outline">
                {t("cancel")}
              </Button>
            </Link>
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <Save className="h-4 w-4" />
                  </motion.div>
                  {t("saving")}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isEdit ? t("updateStaffMember") : t("createStaffMember")}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
